import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useChatStore } from "@/stores/chatStore";
import { useMarketStore } from "@/stores/marketStore";
import { FocusTrap } from "@/components/a11y/FocusTrap";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onToggleSidebar: () => void;
  onLoadConversation: (id: string) => void;
}

interface PaletteCommand {
  id: string;
  label: string;
  category: string;
  shortcut?: string;
  action: () => void;
}

function fuzzyScore(target: string, query: string): number {
  const t = target.toLowerCase();
  const q = query.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length ? 20 : 0;
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const mod = isMac ? "\u2318" : "Ctrl+";

export function CommandPalette({
  open,
  onClose,
  onNewChat,
  onToggleSidebar,
  onLoadConversation,
}: CommandPaletteProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const conversations = useChatStore((s) => s.conversations);
  const { setMarket } = useMarketStore();

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "dark";

  const toggleTheme = useCallback(() => {
    const html = document.documentElement;
    const next = html.getAttribute("data-theme") === "dark" ? "light" : "dark";
    html.setAttribute("data-theme", next);
    localStorage.setItem("ortvis-theme", next);
  }, []);

  const allCommands = useMemo<PaletteCommand[]>(() => {
    const actions: PaletteCommand[] = [
      {
        id: "new-chat",
        label: t("palette.newConversation"),
        category: t("palette.actions"),
        shortcut: `${mod}\u21E7N`,
        action: onNewChat,
      },
      {
        id: "toggle-sidebar",
        label: t("palette.toggleSidebar"),
        category: t("palette.actions"),
        shortcut: `${mod}B`,
        action: onToggleSidebar,
      },
      {
        id: "toggle-theme",
        label: isDark ? t("palette.lightMode") : t("palette.darkMode"),
        category: t("palette.actions"),
        action: toggleTheme,
      },
      {
        id: "market-se",
        label: t("palette.switchToSE"),
        category: t("palette.actions"),
        action: () => setMarket("SE"),
      },
      {
        id: "market-no",
        label: t("palette.switchToNO"),
        category: t("palette.actions"),
        action: () => setMarket("NO"),
      },
    ];

    const convCommands: PaletteCommand[] = conversations.slice(0, 20).map((c) => ({
      id: `conv-${c.conversation_id}`,
      label: c.title,
      category: t("palette.conversations"),
      action: () => onLoadConversation(c.conversation_id),
    }));

    return [...actions, ...convCommands];
  }, [t, isDark, conversations, onNewChat, onToggleSidebar, onLoadConversation, toggleTheme, setMarket]);

  const filtered = useMemo(() => {
    if (!query.trim()) {
      // Show all actions + first 5 conversations
      const actions = allCommands.filter((c) => !c.id.startsWith("conv-"));
      const convs = allCommands.filter((c) => c.id.startsWith("conv-")).slice(0, 5);
      return [...actions, ...convs];
    }
    return allCommands
      .map((cmd) => ({ cmd, score: fuzzyScore(cmd.label, query) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.cmd);
  }, [query, allCommands]);

  // Group by category for rendering
  const groups = useMemo(() => {
    const map = new Map<string, PaletteCommand[]>();
    for (const cmd of filtered) {
      const group = map.get(cmd.category) || [];
      group.push(cmd);
      map.set(cmd.category, group);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [filtered.length]);

  // Scroll active item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.querySelector("[data-active='true']");
    if (item) {
      item.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  function execute(cmd: PaletteCommand) {
    onClose();
    cmd.action();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (cmd) execute(cmd);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  if (!open) return null;

  let itemIndex = 0;

  return (
    <FocusTrap>
      <div className="cx-palette-backdrop animate-cx-fade-in" onClick={onClose} />
      <div className="cx-palette animate-cx-fade-in" onKeyDown={handleKeyDown}>
        <input
          ref={inputRef}
          className="cx-palette-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("palette.placeholder")}
          aria-label={t("palette.placeholder")}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls="palette-list"
          aria-activedescendant={filtered[activeIndex] ? `palette-item-${filtered[activeIndex].id}` : undefined}
        />
        <div ref={listRef} id="palette-list" role="listbox" className="cx-palette-list">
          {groups.map(([category, commands]) => (
            <div key={category}>
              <div className="cx-settings-section-label cx-palette-category">
                {category}
              </div>
              {commands.map((cmd) => {
                const idx = itemIndex++;
                return (
                  <div
                    key={cmd.id}
                    id={`palette-item-${cmd.id}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    className={`cx-palette-item${idx === activeIndex ? " cx-palette-item--active" : ""}`}
                    data-active={idx === activeIndex}
                    onClick={() => execute(cmd)}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <span className="cx-palette-label-truncate">
                      {cmd.label}
                    </span>
                    {cmd.shortcut && (
                      <span className="cx-palette-shortcut">{cmd.shortcut}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="cx-palette-empty">
              {t("palette.noResults")}
            </div>
          )}
        </div>
      </div>
    </FocusTrap>
  );
}
